import React, { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { SwitchTransition } from 'react-transition-group';
import styled from 'styled-components';
import { useEventSource } from '../../utils/LocationContext';
import {
    getPageNameByEventSource,
    ymTrackCollectionAddProductPostCloseDialog,
    ymTrackCollectionGetProductFromUrlSubmit,
    ymTrackCollectionGetProductFromUrlCloseDialog,
    ymTrackCollectionGetProductFromUrlError,
    ymTrackCollectionAddProductPostSubmit,
    ymTrackCollectionAddProductPostRemoveImage,
} from '../../utils/metrika';
import { useTranslation } from '../../utils/useTranslation';
import { CircularProgress } from '../feedback/CircularProgress';
import { Drawer, DrawerContent } from '../feedback/Drawer';
import { IconClose } from '../icon/IconClose';
import { Button } from '../input/Button';
import { FormControl } from '../input/FormControl';
import { IconButton } from '../input/IconButton';
import { Input } from '../input/Input';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { MediaCover } from '../media/MediaCover';
import { MediaPreview } from '../posts/MediaPreview';
import { ContentUI, PostUI } from '../posts/types';
import { TransitionSlideUp } from '../transition/TransitionSlideUp';
import { Typography } from '../typography/Typography';
import { CollectionUI } from '../users/types';

const Overlay = styled(Flex)`
    inset: 0;
    background-color: var(--overlay);
    -webkit-tap-highlight-color: transparent;
    width: 100%;
    height: 100%;
    z-index: 1201;
    position: fixed;
`;
enum AddPostDrawerStep {
    FIND_PRODUCT = 'FIND_PRODUCT',
    EDIT_PRODUCT = 'EDIT_PRODUCT',
    CLOSED = 'CLOSED',
}

interface ParsingError {
    data: {
        code: string;
    };
}

interface AddPostDrawerProps {
    collectionId: CollectionUI['id'];
    handleClose: VoidFunction;
    searchProduct: (url: string) => void;
    productLoading: boolean;
    productLoadingError?: Error | AxiosError<ParsingError>;
    newPost?: PostUI;
    savePostToCollection: (post: PostUI) => void;
}

export const AddPostDrawer = (props: AddPostDrawerProps) => {
    const {
        handleClose,
        searchProduct,
        productLoading,
        productLoadingError,
        newPost,
        savePostToCollection,
        collectionId,
    } = props;
    const [productLink, setProductLink] = useState('');
    const t = useTranslation();
    const [step, setStep] = useState<AddPostDrawerStep>(AddPostDrawerStep.FIND_PRODUCT);
    const [loadingError, setLoadingError] = useState<{ error?: boolean; message?: string }>({});
    const [post, setPost] = useState<PostUI | undefined>(newPost);
    const { eventSource } = useEventSource();

    const onClose = useCallback(() => {
        if (step === AddPostDrawerStep.FIND_PRODUCT && eventSource) {
            ymTrackCollectionGetProductFromUrlCloseDialog({
                collectionId,
                page: getPageNameByEventSource(eventSource),
            });
        }

        if (step === AddPostDrawerStep.EDIT_PRODUCT && eventSource) {
            ymTrackCollectionAddProductPostCloseDialog({
                collectionId,
                page: getPageNameByEventSource(eventSource),
            });
        }

        setStep(AddPostDrawerStep.CLOSED);
        handleClose();
    }, [handleClose, collectionId]);

    const searchProductCallback = useCallback(() => {
        if (eventSource) {
            ymTrackCollectionGetProductFromUrlSubmit({
                collectionId,
                page: getPageNameByEventSource(eventSource),
            });
        }

        searchProduct(productLink);
    }, [productLink, searchProduct, eventSource, collectionId]);

    const onUrlChange = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            setLoadingError({
                error: false,
                message: undefined,
            });
            setProductLink(e.currentTarget.value);
        },
        [setProductLink],
    );

    const savePostToCollectionCallback = useCallback(() => {
        if (post) {
            if (eventSource) {
                ymTrackCollectionAddProductPostSubmit({
                    collectionId,
                    page: getPageNameByEventSource(eventSource),
                });
            }

            savePostToCollection(post);
            onClose();
        }
    }, [post, savePostToCollection, onClose, eventSource, collectionId]);

    useEffect(() => {
        if (newPost) {
            setStep(AddPostDrawerStep.EDIT_PRODUCT);
            setPost(newPost);
        }
    }, [newPost]);

    useEffect(() => {
        if (productLoadingError) {
            if (productLoadingError instanceof AxiosError) {
                if (productLoadingError.response?.data.code === '404') {
                    setLoadingError({
                        error: true,
                        message:
                            'Не смогли создать пост из товара. Проверьте, что по ссылке действительно находится товар, или попробуйте другую ссылку.',
                    });
                } else if (productLoadingError.response?.status === 429) {
                    setLoadingError({
                        error: true,
                        message: 'Вы добавили слишком много постов. Попробуйте через минуту.',
                    });
                }
            } else {
                setLoadingError({
                    error: true,
                    message: 'Кажется, что-то пошло не так. Попробуйте позже.',
                });
            }
        }

        if (eventSource && productLoadingError) {
            ymTrackCollectionGetProductFromUrlError({
                collectionId,
                page: getPageNameByEventSource(eventSource),
                reason: productLoadingError.message,
            });
        }
    }, [productLoadingError, eventSource, collectionId]);

    const onRemoveImage = useCallback(
        (mediaUI: ContentUI) => {
            if (post) {
                if (eventSource) {
                    ymTrackCollectionAddProductPostRemoveImage({
                        collectionId,
                        page: getPageNameByEventSource(eventSource),
                    });
                }

                setPost({
                    ...post,
                    content: post.content.filter((contentItem) => contentItem.id !== mediaUI.id),
                });
            }
        },
        [post, setPost, collectionId, eventSource],
    );

    return (
        <SwitchTransition>
            <TransitionSlideUp key={step}>
                <Drawer onClose={onClose}>
                    {step === AddPostDrawerStep.FIND_PRODUCT && (
                        <DrawerContent p={2} position="relative">
                            {productLoading && (
                                <Overlay>
                                    <Box
                                        position="absolute"
                                        top="50%"
                                        left="50%"
                                        zIndex={1210}
                                        transform="translate(-50%, -50%)"
                                    >
                                        <CircularProgress />
                                    </Box>
                                </Overlay>
                            )}

                            <Flex
                                width="100%"
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                pb={2}
                            >
                                <Typography variant="title3" weight="bold">
                                    {t('Добавить пост')}
                                </Typography>
                                <Box>
                                    <IconButton onClick={onClose}>
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            </Flex>
                            <Typography pb={2}>
                                {t(
                                    'Добавляйте и сохраняйте понравившиеся товары с любых сайтов по ссылке на них',
                                )}
                            </Typography>
                            <FormControl
                                width="100%"
                                mb={2}
                                label={t('Вставьте ссылку на товар')}
                                error={loadingError.error}
                                message={loadingError.message}
                            >
                                <Input
                                    placeholder="https://"
                                    autoFocus
                                    value={productLink}
                                    type="text"
                                    autoComplete="off"
                                    onChange={onUrlChange}
                                />
                            </FormControl>
                            <Box pt={1} width="100%">
                                <Button
                                    width="100%"
                                    color="primary"
                                    disabled={productLink === '' || productLoading}
                                    onClick={searchProductCallback}
                                >
                                    {t('Добавить пост')}
                                </Button>
                            </Box>
                        </DrawerContent>
                    )}
                    {step === AddPostDrawerStep.EDIT_PRODUCT && (
                        <DrawerContent p={2} pr={0}>
                            <Flex
                                width="100%"
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                pb={2}
                                pr={2}
                            >
                                <Typography variant="title3" weight="bold">
                                    {t('Добавить пост')}
                                </Typography>
                                <Box>
                                    <IconButton onClick={onClose}>
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            </Flex>
                            {post?.content && (
                                <Flex
                                    pb={2}
                                    disableScrollbar
                                    direction="row"
                                    maxWidth="100%"
                                    overflowY="auto"
                                    spacing={1}
                                >
                                    {post?.content.map((mediaUI, index) => {
                                        if (!mediaUI) {
                                            return null;
                                        }

                                        return (
                                            <Box
                                                key={mediaUI.id}
                                                draggable
                                                minWidth={152}
                                                cursor="pointer"
                                                position="relative"
                                                overflow="hidden"
                                                borderRadius={2}
                                                data-position={index}
                                                border="2px solid transparent"
                                                transform="translate3d(0,0, 0)"
                                            >
                                                <MediaCover ratio={1} width="100%">
                                                    <MediaPreview media={mediaUI} />
                                                </MediaCover>
                                                {post?.content.length > 1 && (
                                                    <Box position="absolute" top={1} right={1}>
                                                        <IconButton
                                                            size="xs"
                                                            shape="rect"
                                                            variant="filled"
                                                            onClick={(event) => {
                                                                onRemoveImage(mediaUI);
                                                                event.stopPropagation();
                                                            }}
                                                        >
                                                            <IconClose />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Flex>
                            )}
                            <Flex py={1} spacing={0.5} pr={2}>
                                <Typography variant="caption" color="secondary">
                                    {t('Описание')}
                                </Typography>
                                <Typography variant="caption" color="secondary">
                                    {post?.text}
                                </Typography>
                            </Flex>
                            <Box pt={1} pr={2} width="100%">
                                <Button
                                    width="100%"
                                    color="primary"
                                    onClick={savePostToCollectionCallback}
                                >
                                    {t('Сохранить')}
                                </Button>
                            </Box>
                        </DrawerContent>
                    )}
                </Drawer>
            </TransitionSlideUp>
        </SwitchTransition>
    );
};
